/**
 * Ask layer, BROWSER adapter: the whole Ask layer with NO backend. The primary way to
 * adopt it.
 *
 *            `core/`. Then make `ask-api.ts` a one-liner:
 *                export { searchAssistant, sendAssistantFeedback } from './ask-client';
 *            The two functions have exactly the signatures `ask-api.ts` exports, so `HelpAsk`
 *            does not change.
 * Run:       in the browser bundle. Vite/webpack/esbuild resolve the `.js` specifiers to the `.ts`
 *            files; the generated `catalog.data.ts` is bundled like any other module.
 * EDIT:      (1) the three import paths; (2) `setAskPrincipal()` — call it from your auth state
 *            so roles can rank-boost; (3) `setTelemetrySink()` — point the miss log somewhere.
 *
 * When to choose this: any stack. The server can be ASP.NET, Java, PHP, a static host — nothing
 * runs there. The question a user types never leaves the browser. The catalog is reviewed in PRs
 * and compiled into the bundle, so a deploy is still what changes the answers.
 *
 * What you lose: the SERVER-side miss log. By default the two telemetry events go to the console.
 * To keep the steering instrument, give `setTelemetrySink` something that posts — your analytics
 * SDK (Application Insights JS, Segment, Plausible events…) or a tiny endpoint of your own that
 * appends `{event, properties, measurements}` to a table. `matchFound: false` rows are the gap
 * report; without them you are tuning the catalog blind.
 *
 * Roles here come from the CLIENT's auth state, which a user controls. That is fine: roles only
 * reorder results and never hide an entry, so the most a tampered role can do is reorder the
 * user's own answers.
 */
// EDIT: after adoption — the path to where you saved `core/`, e.g. `'./ask/handlers'`.
import { handleFeedback, handleSearch } from './core/handlers';
import type {
  AssistantFeedback,
  AssistantFeedbackResponse,
  AssistantSearchRequest,
  AssistantSearchResponse,
  Principal,
  TenantId,
} from './core/types';
import type { ValidationErrorBody } from './core/handlers';

/**
 * Where the two product events go. Default: `console.info`.
 * EDIT: e.g. `setTelemetrySink({ track: (name, props, measures) => appInsights.trackEvent({ name }, { ...props, ...measures }) })`.
 */
export { setTelemetrySink, type TelemetrySink } from './core/interaction-log';

let principal: Principal | null = null;
let tenantId: TenantId = null;

/**
 * EDIT: call once your auth state is known — `setAskPrincipal({ id: user.id, roles: user.roles })`
 * — and again on sign-out with `null`. The id collapses one person's keystroke burst into one
 * miss-log row; the roles rank-boost. Both optional in effect.
 */
export const setAskPrincipal = (next: Principal | null, tenant: TenantId = null): void => {
  principal = next;
  tenantId = tenant;
};

/** Thrown when the body the panel built does not validate — a programming error, not a user one. */
export class AskClientError extends Error {
  constructor(
    readonly status: number,
    readonly body: unknown,
  ) {
    super(
      `ask-client: ${status} ${
        (body as Partial<ValidationErrorBody>)?.error?.issues
          ?.map((issue) => `${issue.path || '(root)'}: ${issue.message}`)
          .join('; ') ?? 'request rejected'
      }`,
    );
    this.name = 'AskClientError';
  }
}

/** Same signature as `ask-api.ts`'s `searchAssistant` — computed here, no request. */
export const searchAssistant = async (args: AssistantSearchRequest): Promise<AssistantSearchResponse> => {
  const result = await handleSearch(args, principal, tenantId);
  if (result.status !== 200) throw new AskClientError(result.status, result.body);
  return result.body as AssistantSearchResponse;
};

/** Same signature as `ask-api.ts`'s `sendAssistantFeedback`. */
export const sendAssistantFeedback = async (args: AssistantFeedback): Promise<AssistantFeedbackResponse> => {
  const result = await handleFeedback(args, principal, tenantId);
  if (result.status !== 202) throw new AskClientError(result.status, result.body);
  return result.body as AssistantFeedbackResponse;
};
